import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';
import { isAllowedCertificateFile } from '../utils/certificateHelpers';

const { TextArea } = Input;
const { Text } = Typography;

export default function DocumentFormModal({
  open,
  onCancel,
  onSubmit,
  editingDocument = null,
  supplier,
  currentUser,
}) {
  const { t } = useI18n();
  const [form] = Form.useForm();
  const isEditing = Boolean(editingDocument);

  const [fileList, setFileList] = useState([]);
  const [existingFile, setExistingFile] = useState(null);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: editingDocument?.name || '',
      description: editingDocument?.description || '',
    });
    setFileList([]);
    setExistingFile(editingDocument
      ? { fileName: editingDocument.fileName, fileUrl: editingDocument.fileUrl }
      : null);
    setFileError('');
  }, [editingDocument, form, open]);

  const beforeUpload = (file) => {
    if (!isAllowedCertificateFile(file)) {
      setFileError(t('File must be one of .pdf, .png, .jpg, .jpeg, .doc, .docx and at most 10 MB'));
      return false;
    }
    setFileError('');
    setFileList([file]);
    return false;
  };

  const handleRemoveFile = () => {
    setFileList([]);
  };

  const handleFinish = async () => {
    const values = await form.validateFields();

    if (fileList.length === 0 && !existingFile) {
      setFileError(t('A file is required'));
      return;
    }

    const newFile = fileList[0] || null;
    const documentData = {
      ...(editingDocument || {}),
      supplierId: supplier?.id,
      name: values.name.trim(),
      description: values.description || '',
      ...(newFile
        ? {
          fileName: newFile.name,
          fileUrl: URL.createObjectURL(newFile),
          uploadedBy: currentUser?.name || 'System',
          uploadDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
        }
        : {}),
    };

    const success = onSubmit(documentData);
    if (success !== false) {
      setFileList([]);
      setFileError('');
    }
  };

  const currentFileLabel = fileList[0]?.name || existingFile?.fileName;

  return (
    <Modal
      title={isEditing ? t('Edit Document') : t('Add Document')}
      open={open}
      onCancel={onCancel}
      width={560}
      footer={[
        <Button key="cancel" onClick={onCancel}>{t('Close')}</Button>,
        <Button key="submit" type="primary" onClick={handleFinish}>
          {isEditing ? t('Update') : t('Create')}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('Name')}
          name="name"
          rules={[
            { required: true, message: t('Name is required') },
            {
              validator: (_, value) => (
                value && value.trim().length === 0
                  ? Promise.reject(new Error(t('Name is required')))
                  : Promise.resolve()
              ),
            },
            { max: 255, message: t('Name must be at most 255 characters') },
          ]}
        >
          <Input placeholder={t('Document name')} />
        </Form.Item>
        <Form.Item label={t('Description')} name="description">
          <TextArea rows={3} placeholder={t('Description')} />
        </Form.Item>
        <Form.Item label={t('File Upload')} required>
          <Upload
            beforeUpload={beforeUpload}
            onRemove={handleRemoveFile}
            fileList={fileList}
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>{t('Select File')}</Button>
          </Upload>
          {!fileList.length && existingFile && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {t('Current file')}: {currentFileLabel}
            </Text>
          )}
          {fileError && (
            <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {fileError}
            </Text>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
}
